import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useResetPassword } from '@/hooks/useAuth';
import PasswordInput from '@/components/PasswordInput';
import { MIN_PASSWORD_LENGTH } from 'shared';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const resetPassword = useResetPassword();

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Link de reposição inválido');
    }
  }, [token]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Link de reposição inválido');
      return;
    }

    if (password !== confirmPassword) {
      setError('As palavras-passe não coincidem');
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`A palavra-passe tem de ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres`);
      return;
    }

    resetPassword.mutate(
      { token, newPassword: password },
      {
        onSuccess: () => setSuccess(true),
      }
    );
  };

  if (success) {
    return (
      <>
        <Helmet>
          <title>Repor palavra-passe | DIGGER</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div>
              <h1 className="text-center text-3xl font-bold text-foreground">
                DIGGER
              </h1>
              <h2 className="mt-6 text-center text-2xl font-semibold text-foreground">
                Palavra-passe reposta ✅
              </h2>
            </div>

            <div role="status" aria-live="polite" className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 p-4 rounded-md text-sm">
              <p>A tua palavra-passe foi reposta com sucesso. Já podes iniciar sessão com a nova.</p>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="btn-primary w-full"
            >
              Iniciar sessão
            </button>
          </div>
        </div>
      </>
    );
  }

  if (!token) {
    return (
      <>
        <Helmet>
          <title>Repor palavra-passe | DIGGER</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8">
            <div>
              <h1 className="text-center text-3xl font-bold text-foreground">
                DIGGER
              </h1>
              <h2 className="mt-6 text-center text-2xl font-semibold text-foreground">
                Link de reposição inválido
              </h2>
            </div>

            <div role="alert" aria-live="assertive" className="bg-destructive/10 text-destructive p-4 rounded-md text-sm">
              <p>Este link para repor a palavra-passe é inválido ou já expirou. Pede um novo.</p>
            </div>

            <Link to="/forgot-password" className="btn-primary w-full block text-center">
              Pedir novo link
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Repor palavra-passe | DIGGER</title>
      </Helmet>
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h1 className="text-center text-3xl font-bold text-foreground">
              DIGGER
            </h1>
            <h2 className="mt-6 text-center text-2xl font-semibold text-foreground">
              Repor palavra-passe
            </h2>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              Escreve a tua nova palavra-passe.
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit} aria-busy={resetPassword.isPending}>
            <div role="alert" aria-live="assertive" aria-atomic="true">
              {(error || resetPassword.isError) && (
                <div id="reset-password-error" className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
                  {error || resetPassword.error?.message}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="password" className="label">
                  Nova palavra-passe
                </label>
                <PasswordInput
                  id="password"
                  name="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={!!(error || resetPassword.isError) || undefined}
                  aria-describedby={`password-hint${(error || resetPassword.isError) ? ' reset-password-error' : ''}`}
                />
                <p id="password-hint" className="mt-1 text-xs text-muted-foreground">
                  {`Pelo menos ${MIN_PASSWORD_LENGTH} caracteres, com maiúsculas, minúsculas e um número.`}
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="label">
                  Confirma a nova palavra-passe
                </label>
                <PasswordInput
                  id="confirmPassword"
                  name="confirmPassword"
                  revealLabel="confirmação da palavra-passe"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  aria-invalid={!!(error || resetPassword.isError) || undefined}
                  aria-describedby={(error || resetPassword.isError) ? 'reset-password-error' : undefined}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={resetPassword.isPending}
              className="btn-primary w-full"
            >
              {resetPassword.isPending ? 'A repor…' : 'Repor palavra-passe'}
            </button>

            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="text-primary hover:text-primary/80 font-medium">
                Voltar ao início de sessão
              </Link>
            </p>
          </form>
        </div>
      </div>
    </>
  );
}
